import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type ElementSizingMode,
  getActiveBinding,
  isBindingActive,
  type OptionPickerDefaultValue,
  OPTIONS_PICKER_DEFAULT_VALUE_OPTIONS,
  type PropertiesConfig,
  RADIO_LAYOUT_MODE_OPTIONS_WITH_IMAGES,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { RadioDefinitionMapper } from './ai/radio.definition-mapper';
import { RadioElementHandler } from './ai/radio.element-handler';
import { generateRadioFormField } from './generation/form-field';
import { radioV2Migration } from './migrations/002-radio.migration';
import { radioV3Migration } from './migrations/003-radio.migration';
import { radioV4PaddingToMarginMigration } from './migrations/004-radio-padding-to-margin.migration';
import { RadioComponent } from './radio.component';
import { type RadioPropsModel } from './radio.models';
import { RadioPropsBuilder } from './radio.props-builder';
import { TILE_SELECTOR_IMAGE_RATIOS } from './tile-selector-element';

export { transformToRadio } from './radio.transforms';
export type RadioElement = CanvasElementFrom<'radio'>;
export type RadioDef = WithProps<RadioPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    radio: RadioDef;
  }
}

const DEFAULT_WIDTH = 356;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'radio',
  componentType: RadioComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Radio',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial() as PropsOf<'radio'>,
  propsBuilder: RadioPropsBuilder,
  generation: {
    formField: generateRadioFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.image',
    'content.icon',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.radio.readonly',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
    optionsSortSupport: true,
    searchAndFiltersFieldSupport: true,
  },
  ai: {
    name: 'radio',
    handler: () => RadioElementHandler,
    definitionMapper: () => RadioDefinitionMapper,
  },
  version: 4,
  migrations: {
    2: radioV2Migration,
    3: radioV3Migration,
    4: radioV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'optionsList',
        isDefaultDataSourceInput: true,
        bindingDefs: [
          { property: 'value', label: 'Option value field', dataType: 'String', value: true },
          { property: 'label', label: 'Option label field', dataType: 'String', display: true },
        ],
      },
      {
        name: 'selected',
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        // Default schema before binding consists of label, value pair. If bound, this will change.
        schema: {
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: 'label',
            },
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: 'value',
            },
          ],
          id: '',
          name: 'Radio',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'radio'> {
  return {
    content: {
      title: {
        label: 'Radio',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      data: {
        defaultValue: 'none',
      },
      radio: {
        values: [],
        preselect: 'Option 2',
        layout: 'row',
        cardAlign: 'left',
        maxColumns: { mode: 'value', value: 1 },
        minWidth: { mode: 'value', value: 320 },
      },
      customFields: [],
      mode: 'text',
      sort: {
        field: '',
        direction: '',
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      fontWeight: '$t.labelFontWeight',
      fontStyle: [],
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      radio: {
        label: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        padding: '16px',
        gap: '8px',
        tileRadius: '4px',
        checkmarkSize: '24px',
      },
      box: {
        subText: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontSize: '13px',
          lineHeight: '$t.lineHeight',
        },
      },
      card: {
        heading: {
          align: 'left',
        },
        subheading: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '16px',
          fontWeight: '$t.labelFontWeight',
          fontStyle: [],
          spaceAbove: '5px',
          align: 'left',
        },
        description: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '13px',
          fontWeight: '$t.labelFontWeight',
          spaceAbove: '16px',
          align: 'left',
        },
        customField: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '13px',
          spaceAbove: '16px',
          align: 'left',
        },
      },
    },
  };
}

function isImageFieldMissing(data: CanvasElementDataProperties[] | undefined): boolean {
  const binding = getActiveBinding(data, 'optionsList');
  return !!binding && !binding.bindings?.['imageSrc'];
}

function hasCardBindingField(
  data: CanvasElementDataProperties[] | undefined,
  property: string,
): boolean {
  return !!getActiveBinding(data, 'optionsList')?.bindings?.[property];
}

function getPropsConfig(): PropertiesConfig {
  const selectMode = [
    epc.utils.observedField(
      epc.fields.selectFieldFromImageList(
        'checkbox-template',
        'content.mode',
        'Template',
        RADIO_LAYOUT_MODE_OPTIONS_WITH_IMAGES,
        { allowNone: false, showSelection: true, hideLabel: true },
      ),
      (_, value) => {
        if (value === 'card') {
          return {
            //heading
            'style.fontSize': '20px',
            'style.fontWeight': '600',
            //subheading
            'style.card.subheading.fontSize': '16px',
            'style.card.subheading.fontWeight': '400',
            //description
            'style.card.description.fontSize': '13px',
            'style.card.description.fontWeight': '400',

            'style.radio.padding': '16px',
            'style.radio.gap': '8px',
            'style.radio.tileRadius': '4px',
            'style.radio.checkmarkSize': '16px',

            'content.showCheckIcon': true,

            'content.image.ratio': 'circle',
            'content.image.position': 'right',
            'content.image.width': '32px',
          };
        } else if (value === 'image') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.radio.padding': '16px',
            'style.radio.gap': '8px',
            'style.radio.tileRadius': '4px',
            'style.radio.checkmarkSize': '24px',

            'content.image.ratio': '1:1',
            'content.image.position': 'top',
            'content.image.width': '80px',
          };
        } else if (value === 'box') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.radio.padding': '16px',
            'style.radio.gap': '8px',
            'style.radio.tileRadius': '4px',
            'style.radio.checkmarkSize': '24px',

            'content.icon.position': 'left',
            'content.icon.size': '24px',
          };
        } else if (value === 'chip') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.radio.padding': '8px',
            'style.radio.gap': '4px',
            'style.radio.tileRadius': '25px',
            'style.radio.checkmarkSize': '24px',
          };
        } else {
          // value === 'text'
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.radio.padding': '16px',
            'style.radio.gap': '8px',
            'style.radio.tileRadius': '4px',
            'style.radio.checkmarkSize': '24px',
          };
        }
      },
    ),
  ];

  return epc.initConfig('radio', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:optionsList.enabled',
        'Data',
        [
          epc.utils.conditionalField(
            epc.fields.tileWithCheckboxItemValues('content.radio', 'Values', {
              withSubText: true,
            }),
            'content.mode',
            (mode, data) => mode === 'card' && !getActiveBinding(data, 'optionsList'),
          ),
          epc.utils.conditionalField(
            epc.utils.separator('', { labelOnLine: 'Set dynamic source' }),
            'content.mode',
            (mode) => mode !== 'card',
          ),
        ],
        [
          epc.fields.selectDataSourceField({
            key: 'optionsList',
            label: 'Values',
            sources: 'list',
            bindings: [
              { property: 'value', label: 'Option value field', dataType: 'String', value: true },
              {
                property: 'imageSrc',
                label: 'Option image field',
                dataType: 'String',
                value: true,
              },
              {
                property: 'icon',
                label: 'Option icon field',
                dataType: 'String',
                value: true,
              },
              { property: 'label', label: 'Option value field', dataType: 'String', display: true },
            ],
          }),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'data.?key:optionsList.bindings.label',
                'Display',
                'data.?key:optionsList.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.imageSrc',
                  'Image',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'image' && mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.subheading',
                  'Subheading',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.subheading',
                  'Sub text',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'box',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.description',
                  'Description',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.icon',
                  'Icon',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'box',
              ),
              epc.fields.sortField(
                'content.sort',
                'Sort by',
                'data.?key:optionsList.dataSource.columns',
                'name',
                COLUMN_ID_OR_NAME,
              ),
              epc.utils.conditionalField(
                epc.utils.separator(),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.listCustomFieldsField('content.customFields', 'Add Custom Field', {
                  source: 'schema',
                  datasourcePath: 'data.?key:optionsList.dataSource',
                  useMetaValue: false,
                }),
                'content.mode',
                (mode) => mode !== 'card',
              ),
            ],
            'data.?key:optionsList',
            (value) => !isBindingActive(value),
          ),
        ],
      ),

      epc.subsections.subsection('content', 'Template', selectMode),

      epc.subsections.subsection(
        'content.radio',
        'Radio',
        [
          epc.fields.selectFieldFromList(
            'content.data.defaultValue',
            'Default value',
            OPTIONS_PICKER_DEFAULT_VALUE_OPTIONS,
            {
              showSelection: true,
              allowNone: false,
              optionsFilter: (model: PropsOf<'dropdown'>, options) =>
                !getValueByPath(model, `data.?key:optionsList.enabled`)
                  ? (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                      (o) => o.value !== 'placeholder',
                    )
                  : (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                      (o) => o.value !== 'placeholder' && o.value !== 'fixedValue',
                    ),
            },
          ),

          epc.utils.conditionalField(
            // pick from list of `values`
            epc.fields.selectFieldFromModel(
              'content.radio.preselect',
              'Value',
              'content.radio.values',
              { allowNone: false, showSelection: true },
            ),
            'content.data.defaultValue',
            (modelValue) => (modelValue as OptionPickerDefaultValue) !== 'fixedValue',
          ),

          epc.fields.layoutField('content.radio.layout'),

          ...epc.utils.conditionalFields(
            [
              epc.fields.sizeField({
                path: 'content.radio.maxColumns',
                label: 'Max columns',
                units: [{ min: 1, max: 10 }],
                sizingOptions: [{ label: 'Min', value: 'min' }],
                showArrows: true,
              }),
              epc.fields.sizeField({
                path: 'content.radio.minWidth',
                label: 'Min width',
                units: [{ max: 400, min: 100, unit: 'px' }],
                sizingOptions: [{ label: 'Min', value: 'min' }],
                showArrows: true,
              }),
            ],
            'content.radio.layout',
            (target) => target !== 'column',
          ),

          epc.fields.positionField(
            'content.image.position',
            'Image position',
            [
              { value: 'right', label: '', icon: 'icon_aligns_to_left_16' },
              { value: 'left', label: '', icon: 'icon_aligns_to_right_16' },
            ],
            { defaultValue: 'right' },
            {
              hideWhen: (model) => model.content.mode !== 'card',
            },
          ),

          epc.fields.toggleField('content.showCheckIcon', 'Show checkmark icon', {
            hideWhen: (model) => ['text', 'image', 'chip'].includes(model.content.mode ?? ''),
          }),
          epc.utils.conditionalField(
            epc.fields.horizontalAlignField(`content.radio.cardAlign`, {
              defaultValue: undefined,
              label: 'Card Alignment',
            }),
            'content',
            (modelValue: RadioPropsModel['content']) =>
              !(
                ['image', 'box'].includes(modelValue.mode ?? '') &&
                modelValue.radio.layout === 'column'
              ),
          ),

          epc.fields.toggleField('content.radio.readonly', 'Read Only'),

          epc.utils.conditionalField(
            epc.utils.separator(),
            'content.mode',
            (value, model) =>
              value === 'text' ||
              value === 'chip' ||
              (value === 'card' && !isBindingActive(model.data)) ||
              ((value === 'image' || value === 'card') && isImageFieldMissing(model.data)),
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromList(
                'content.image.ratio',
                'Ratio',
                TILE_SELECTOR_IMAGE_RATIOS,
                {
                  allowNone: false,
                  showSelection: true,
                },
              ),
              epc.utils.conditionalField(
                epc.fields.positionField(
                  'content.image.position',
                  'Position',
                  [
                    { value: 'top', label: 'Top', icon: 'icon_aligns_to_top_16' },
                    { value: 'bottom', label: 'Bottom', icon: 'icon_aligns_to_bottom_16' },
                    { value: 'left', label: 'Left', icon: 'icon_aligns_to_left_16' },
                    { value: 'right', label: 'Right', icon: 'icon_aligns_to_right_16' },
                  ],
                  { defaultValue: 'left' },
                  { renderType: 'icon' },
                ),
                'content.mode',
                (mode) => mode !== 'image',
              ),
              epc.fields.sliderField('content.image.width', 'Width', {
                units: [{ min: 20, max: 800, unit: 'px' }],
              }),
              epc.fields.sliderField('content.image.height', 'Height', {
                units: [{ min: 20, max: 800, unit: 'px' }],
                disableWhen: (model) => {
                  if (getValueByPath(model, 'content.image.ratio') !== 'none') {
                    return 'available only for unrestricted ratio';
                  }
                  return undefined;
                },
              }),
              epc.fields.imageBehaviorField('content.image.behavior'),
            ],
            'content.mode',
            (mode, model) =>
              (mode !== 'image' && mode !== 'card') || isImageFieldMissing(model.data),
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.positionField(
                'content.icon.position',
                'Position',
                [
                  { value: 'top', label: 'Top', icon: 'icon_aligns_to_top_16' },
                  { value: 'bottom', label: 'Bottom', icon: 'icon_aligns_to_bottom_16' },
                  { value: 'left', label: 'Left', icon: 'icon_aligns_to_left_16' },
                  { value: 'right', label: 'Right', icon: 'icon_aligns_to_right_16' },
                ],
                { defaultValue: 'left' },
                { renderType: 'icon' },
              ),
              epc.fields.sliderField('content.icon.size', 'Size', {
                units: [{ min: 8, max: 100, unit: 'px' }],
              }),
            ],
            'content.mode',
            (mode) => mode !== 'box',
          ),
        ],
        {
          dataBindings: [
            {
              key: 'selected',
              label: 'Selected Value',
              sources: 'field',
              bindings: [{ property: 'selected', label: 'Selected value', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection(
          'style.radio',
          'Radio',
          [
            epc.fields.colorField('style.radio.fill', 'Fill', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.colorField('style.radio.border', 'Border', { allowGradient: false }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'text';
            },
          },
        ),
        epc.subsections.subsection(
          'style.radio',
          'Box',
          [
            epc.fields.colorField('style.radio.fill', 'Background', { allowNone: false }),
            epc.fields.colorField('style.radio.border', 'Outline', { allowGradient: false }),
            epc.fields.radiusField('style.radio.tileRadius'),
            epc.utils.conditionalField(
              epc.fields.sliderField('style.radio.checkmarkSize', 'Checkmark size', [
                { min: 8, max: 48, unit: 'px' },
              ]),
              'content.mode',
              (mode, model) =>
                !['box', 'card'].includes(mode) || !getValueByPath(model, 'content.showCheckIcon'),
            ),
            epc.fields.sliderField('style.radio.padding', 'Padding', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.radio.gap', 'Space between', [
              { min: 0, max: 72, unit: 'px' },
            ]),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode === 'text';
            },
          },
        ),
        epc.subsections.subsection('style.radio', 'Options', [
          epc.fields.colorField('style.radio.label', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields('style.fontFamily', 'style.fontWeight'),
          epc.fields.fontStyleField('style.fontStyle', 'Style', ['bold']),
          epc.fields.sliderField(`style.fontSize`, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
          epc.utils.conditionalField(
            epc.fields.horizontalAlignField('style.card.heading.align', { label: 'Alignment' }),
            'content.mode',
            (mode) => mode !== 'card',
          ),
        ]),

        epc.subsections.subsection(
          'style.box.subText',
          'Sub text',
          [
            epc.fields.selectFieldFromFormFonts('style.box.subText.fontFamily', 'Font'),
            epc.fields.colorField('style.box.subText.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.utils.observedField(
              epc.fields.sliderField('style.box.subText.fontSize', 'Size', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: RadioPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateLineHeightWithFontSize(props, value, 'style.box.subText.lineHeight');
              },
            ),
            epc.utils.observedField(
              epc.fields.sliderField('style.box.subText.lineHeight', 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: RadioPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(props, value, 'style.box.subText.fontSize');
              },
            ),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              if (mode !== 'box') {
                return true;
              }
              if (getActiveBinding(model.data, 'optionsList')) {
                return !hasCardBindingField(model.data, 'subheading');
              }
              const subheadings =
                (getValueByPath(model, 'content.radio.subheading') as
                  | (string | undefined)[]
                  | undefined) ?? [];
              return !subheadings.some((subheading) => !!subheading);
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.subheading',
          'Card Subheading',
          [
            epc.fields.colorField('style.card.subheading.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.selectFieldFromFontWeights(
              'style.card.subheading.fontWeight',
              'Font Weight',
              'style.fontFamily',
            ),
            epc.fields.fontStyleField('style.card.subheading.fontStyle', 'Style', ['bold']),
            epc.fields.sliderField(`style.card.subheading.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.subheading.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.subheading.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'card' || !hasCardBindingField(model.data, 'subheading');
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.description',
          'Card Description',
          [
            epc.fields.colorField('style.card.description.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.selectFieldFromFontWeights(
              'style.card.description.fontWeight',
              'Font Weight',
              'style.fontFamily',
            ),
            epc.fields.sliderField(`style.card.description.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.description.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.description.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'card' || !hasCardBindingField(model.data, 'description');
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.customField',
          'Card Custom Fields',
          [
            epc.fields.colorField('style.card.customField.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.sliderField(`style.card.customField.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.customField.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.customField.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              const customFields = getValueByPath(model, 'content.customFields') as
                | unknown[]
                | undefined;
              return mode !== 'card' || !customFields?.length;
            },
          },
        ),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function getInitial(): PartialPropsOf<'radio'> {
  return {
    content: {
      title: {
        width: '35%',
      },
      radio: {
        values: ['Option 1', 'Option 2', 'Option 3'],
      },
      mode: 'text',
      image: {
        width: '100px',
        height: '100px',
        ratio: 'none',
        behavior: 'cover',
      },
      icon: {
        size: '16px',
        position: 'left',
      },
    },
  };
}
